import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateCreditCustomerMailSuppressionResponse,
  CreditCustomerMailDeliveriesResponse,
  CreditCustomerMailSettingsResponse,
  CreditCustomerMailSuppressionsResponse,
  DeleteCreditCustomerMailSuppressionResponse,
  UpdateCreditCustomerMailSettingsRequest,
} from '@nadoutong/contracts';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

const SETTINGS_KEY = ['credit-customer-mail-settings'] as const;
const SUPPRESSIONS_KEY = ['credit-customer-mail-suppressions'] as const;

export function creditMailDeliveriesQueryKey(orgId: string): readonly [string, string] {
  return ['credit-customer-mail-deliveries', orgId];
}

export function useCreditCustomerMailSettings(
  enabled = true,
): UseQueryResult<CreditCustomerMailSettingsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: ({ signal }) =>
      opsApiRequest<CreditCustomerMailSettingsResponse>(
        '/v1/ops/credit-customer-mail/settings',
        { signal },
        fetchImpl,
      ),
    enabled,
  });
}

export function useUpdateCreditCustomerMailSettings(): UseMutationResult<
  CreditCustomerMailSettingsResponse,
  Error,
  UpdateCreditCustomerMailSettingsRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      opsApiRequest<CreditCustomerMailSettingsResponse>(
        '/v1/ops/credit-customer-mail/settings',
        { method: 'PATCH', body },
        fetchImpl,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SETTINGS_KEY });
    },
  });
}

export function useCreditCustomerMailDeliveries(
  orgId: string,
  enabled = true,
): UseQueryResult<CreditCustomerMailDeliveriesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: creditMailDeliveriesQueryKey(orgId),
    queryFn: ({ signal }) =>
      opsApiRequest<CreditCustomerMailDeliveriesResponse>(
        `/v1/ops/customers/${encodeURIComponent(orgId)}/credit/mail-deliveries`,
        { signal },
        fetchImpl,
      ),
    enabled,
  });
}

export function useCreditCustomerMailSuppressions(
  enabled = true,
): UseQueryResult<CreditCustomerMailSuppressionsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: SUPPRESSIONS_KEY,
    queryFn: ({ signal }) =>
      opsApiRequest<CreditCustomerMailSuppressionsResponse>(
        '/v1/ops/credit-customer-mail/suppressions',
        { signal },
        fetchImpl,
      ),
    enabled,
  });
}

export function useCreateCreditCustomerMailSuppression(): UseMutationResult<
  CreateCreditCustomerMailSuppressionResponse,
  Error,
  { readonly accountId: string; readonly reason?: string | null }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, reason }) =>
      opsApiRequest<CreateCreditCustomerMailSuppressionResponse>(
        `/v1/ops/credit-customer-mail/suppressions/${encodeURIComponent(accountId)}`,
        { method: 'PUT', body: { reason: reason ?? null } },
        fetchImpl,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUPPRESSIONS_KEY });
    },
  });
}

export function useDeleteCreditCustomerMailSuppression(): UseMutationResult<
  DeleteCreditCustomerMailSuppressionResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountId) =>
      opsApiRequest<DeleteCreditCustomerMailSuppressionResponse>(
        `/v1/ops/credit-customer-mail/suppressions/${encodeURIComponent(accountId)}`,
        { method: 'DELETE' },
        fetchImpl,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUPPRESSIONS_KEY });
    },
  });
}
